import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';

interface IRevenueMonth {
  month: string; // "2026-03"
  total: number;
}

interface Props {
  data: IRevenueMonth[];
  loading: boolean;
}

export const RevenueChartWidget: React.FC<Props> = ({ data, loading }) => {
  const intl = useIntl();

  const maxTotal = useMemo(() => Math.max(...data.map((d) => d.total), 1), [data]);

  const formatMonth = (monthStr: string) => {
    const parts = monthStr.split('-');
    const d = new Date(parseInt(parts[0] ?? '0'), parseInt(parts[1] ?? '1') - 1, 1);
    return d.toLocaleDateString(intl.locale, { month: 'short' });
  };

  const totalRevenue = useMemo(() => data.reduce((sum, d) => sum + d.total, 0), [data]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const formatMonthLong = (monthStr: string) => {
    const parts = monthStr.split('-');
    const d = new Date(parseInt(parts[0] ?? '0'), parseInt(parts[1] ?? '1') - 1, 1);
    return d.toLocaleDateString(intl.locale, { month: 'long', year: 'numeric' });
  };

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.revenue' })}
      icon="pi pi-chart-bar"
      iconColor="#22c55e"
      loading={loading}
    >
      {data.length === 0 || totalRevenue === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noRevenueData' })}
        </p>
      ) : (
        <div>
          {/* Total */}
          <div className="flex align-items-baseline gap-1 mb-3">
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#22c55e', letterSpacing: '-0.02em' }}>
              ${totalRevenue.toLocaleString()}
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)' }}>
              {intl.formatMessage({ id: 'dashboard.lastMonths' }, { count: data.length })}
            </span>
          </div>

          {/* Bar chart */}
          <div className="flex gap-1" style={{ height: '140px' }}>
            {data.map((item, idx) => {
              const isHovered = hoverIndex === idx;
              return (
                <div
                  key={item.month}
                  className="flex flex-column align-items-center"
                  style={{ flex: 1, height: '100%', justifyContent: 'flex-end', position: 'relative' }}
                  onMouseEnter={() => setHoverIndex(idx)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  {isHovered && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-0.25rem',
                        left: '50%',
                        transform: 'translate(-50%, -100%)',
                        background: 'var(--ios-glass-bg)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid var(--ios-glass-border)',
                        boxShadow: 'var(--ios-shadow-md)',
                        borderRadius: 'var(--ios-radius-md)',
                        padding: '0.4rem 0.65rem',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.1rem'
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.65rem',
                          color: 'var(--ios-text-tertiary)',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}
                      >
                        {formatMonthLong(item.month)}
                      </span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#22c55e' }}>
                        ${item.total.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      flex: 1,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '2.5rem',
                        height: `${Math.max((item.total / maxTotal) * 100, 4)}%`,
                        background: item.total > 0 ? (isHovered ? '#84cc16' : '#0a0a0a') : 'var(--ios-surface-muted)',
                        borderRadius: 'var(--ios-radius-sm) var(--ios-radius-sm) 0 0',
                        transition: 'height 0.3s ease, background 0.15s ease',
                        minHeight: '4px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: '0.62rem',
                      color: isHovered ? 'var(--ios-text)' : 'var(--ios-text-tertiary)',
                      marginTop: '0.3rem',
                      fontWeight: 600
                    }}
                  >
                    {formatMonth(item.month)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DashboardWidget>
  );
};
